/**
 * Attribute everything currently selected, in one action.
 *
 * The productivity case for the whole feature: an analyst who has filtered a
 * case's feed down to forty transactions from one counterparty wants to say
 * "these are all theirs" once, not forty times.
 *
 * Which "these" is a real question, so the bar asks rather than guesses. The
 * same checkboxes mean "these rows" and "the counterparties in these rows", and
 * the two write to different places. In an entity watch the second is usually
 * the one worth doing — an address that belongs to an org still belongs to it
 * tomorrow, whereas tagging the rows says nothing about the next transaction.
 *
 * Honest about what it is doing. There is no bulk catalog endpoint, so this
 * fires one write per subject — capped, throttled, and reported per subject by
 * {@link applyToMany}, rather than pretending to be atomic.
 *
 * Asks for the attributes first, through the shared flow rather than a local
 * copy of it. This is the surface where a reason earns its keep: forty rows
 * asserted at once is the claim a reviewer is most likely to question later, and
 * the least likely to be reconstructible from memory. It uses the state machine
 * without {@link AttributionEditor} itself, because the editor's chips-and-rows
 * layout does not fit a table toolbar and there is no single subject whose
 * attachments it could draw.
 *
 * Packaged by CAPABILITY, not by surface. Bulk catalog attribution (tags,
 * labels, groups, entities, membership, primaries on many subjects at once) is
 * advanced catalog authoring, a plan feature. Setting a risk band is a write on
 * the ADDRESS ROW — basic for one address, so basic for a selection too. Without
 * `catalog_advanced` the bar therefore offers "Set risk" alone (addresses only,
 * under the address permission) and nothing else. Hosts decide whether row
 * selection has anything to offer with {@link useBulkAttributionAvailable}, the
 * same answer the bar renders on.
 */
import * as React from "react";
import { UserCheck } from "lucide-react";
import {
  AttributionAttributesPanel,
  Button,
  CatalogItemPicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EnumSelect,
  Label,
  notifyError,
  notifySuccess,
  RoleCombobox,
  useCatalogAdvanced,
  usePendingAttribution,
  type AttributionEditorCandidate,
} from "@authe/ui-core";
import {
  KIND_LABEL,
  MAX_BULK_SUBJECTS,
  applyToMany,
  outcomeMessage,
  type AssignmentAttributes,
  type AttributionKind,
  type AttributionSubject,
  type AttributionTarget,
} from "@authe/fe-commons/attribution";
import { RISK_LEVEL_RANK, type RiskLevel } from "@authe/fe-commons/precedence";
import { usePermissions } from "@authe/ui-core/permissions";
import { useMyTeams } from "@/app/useMyTeams";
import { useAddressesApi, useCatalogApi } from "@/api/adapters";
import { RESOURCES } from "@/config";
import { useInvalidateAddressOverlays } from "@/features/overlays";
import { useAttributionController } from "./useAttributionController";
import { useCatalogLoaders } from "./useCatalogCandidates";

/**
 * Every risk band, least to most severe — the same list and the same order as
 * the single-address control (`AddressRiskControl`), read off the one ladder so
 * a band cannot exist in one door onto this field and not the other.
 */
const RISK_OPTIONS = (Object.keys(RISK_LEVEL_RANK) as RiskLevel[])
  .sort((a, b) => (RISK_LEVEL_RANK[a] ?? 0) - (RISK_LEVEL_RANK[b] ?? 0))
  .map((level) => ({ value: level, label: level[0]!.toUpperCase() + level.slice(1) }));

/** One bar per toolbar, so a constant id is enough to label the select. */
const RISK_SELECT_ID = "bulk-attribution-risk";

/**
 * Imperative form, for the confirm dialog's title.
 *
 * Group is the odd one and deliberately so. A catalog group's members are other
 * catalog rows — `member_tag_ids`, `member_label_ids`, `member_entity_ids`,
 * `member_transaction_group_ids` — so it cannot contain an address or a
 * transaction, and this write attaches the group *to* the selection as an
 * overlay. "Group 40 transactions" describes the containment that is not
 * happening; naming the direction is what stops a reader expecting to find
 * those forty rows listed inside the group afterwards.
 */
const IMPERATIVE: Record<AttributionKind, string> = {
  tag: "Tag",
  label: "Label",
  group: "Attach a group to",
  entity: "Attribute",
};

const PAST_TENSE: Record<AttributionKind, string> = {
  tag: "tagged",
  label: "labelled",
  group: "marked with the group",
  entity: "attributed",
};

/**
 * The authoritative stores get their own past tense, because "12 addresses
 * attributed" would describe an overlay — a much smaller claim than the one that
 * just landed on the company map.
 */
const STORE_PAST_TENSE: Partial<Record<AttributionTarget, string>> = {
  membership: "mapped to the entity",
  primary: "made the headline address",
};

/** Which reading of the selection a write applies to. */
export type BulkTarget = "transaction" | "address";

const OTHER: Record<BulkTarget, BulkTarget> = {
  transaction: "address",
  address: "transaction",
};

/**
 * Where the claim is stored — the second, independent axis.
 *
 * {@link BulkTarget} says which rows the write is about; this says what kind of
 * statement it is. The bar wrote `overlay` unconditionally before, which is why
 * mapping a company out meant one address at a time.
 */
const STORE_LABEL: Record<AttributionTarget, string> = {
  overlay: "Overlay",
  membership: "Member",
  primary: "Headline",
};

const STORE_HINT: Record<AttributionTarget, string> = {
  overlay:
    "What we think this is: the catalog row is attached to the selection, " +
    "visible at the scope you write it to. Nothing is moved into it.",
  membership: "The tenant-wide company map: this address belongs to the entity.",
  primary: "The entity's one headline address on each chain.",
};

/**
 * Only entities have a membership row or a headline pointer; a tag has neither.
 * So the store selector and the kind pickers constrain each other, and the bar
 * shows one entity picker rather than four dead ones.
 */
const STORE_KINDS: Record<AttributionTarget, readonly AttributionKind[]> = {
  overlay: ["tag", "label", "group", "entity"],
  membership: ["entity"],
  primary: ["entity"],
};

function targetNoun(target: BulkTarget, count: number): string {
  if (target === "address") return count === 1 ? "address" : "addresses";
  return count === 1 ? "transaction" : "transactions";
}

/** "1 address" / "12 addresses" — the count IS the label, so it has to read right. */
function targetLabel(target: BulkTarget, count: number): string {
  return `${count} ${targetNoun(target, count)}`;
}

export interface BulkAttributionBarProps {
  /**
   * The selection read as the rows themselves.
   *
   * Passed alongside {@link addresses} rather than resolved here, because the
   * bar deliberately does not know how a row becomes a subject — that mapping
   * belongs to the host's row shape, and is unit-tested there.
   */
  transactions: AttributionSubject[];
  /** The same selection read as the distinct counterparties it touches. */
  addresses: AttributionSubject[];
  /** True inside a case the whole tenant can open — raises write visibility. */
  shared: boolean;
  canEdit: boolean;
  /** Refresh the feed so the rows show what was just written. */
  onChanged: () => void;
  /**
   * The entity this case follows, when it follows exactly one. Offered as a
   * single button, because in an entity watch "these are theirs too" is the
   * action being repeated and a search-and-pick for it every time is the tax
   * this whole feature exists to remove.
   */
  preferredEntity?: { id: string; name: string };
  /**
   * Which stores this surface may write to, in the order offered.
   *
   * Overlay only by default. Membership and the headline pointer are tenant-wide
   * authoritative writes, and a feed toolbar is not where anyone decides that
   * forty counterparties ARE an exchange's addresses — the entity page, where the
   * reader is already looking at that entity's map, opts in.
   */
  stores?: readonly AttributionTarget[];
}

/**
 * Whether the bar can offer anything on a host: every bulk action with
 * `catalog_advanced` (under the host's own `canEdit`), else only "Set risk"
 * (under the address permission). A host turns row selection on with this, so a
 * checkbox never selects rows for a bar that would render nothing.
 */
export function useBulkAttributionAvailable(canEdit: boolean): boolean {
  const advanced = useCatalogAdvanced();
  const { can } = usePermissions();
  return advanced ? canEdit : can(RESOURCES.addresses, "edit");
}

export function BulkAttributionBar(props: BulkAttributionBarProps): React.ReactElement | null {
  // Two components, not one with a branch: the catalog bar's hooks need a
  // subject from the selection, and a plan change must swap the whole tree
  // rather than change hook order.
  return useCatalogAdvanced() ? <BulkCatalogAttributionBar {...props} /> : <BulkRiskBar {...props} />;
}

/**
 * Basic plan: "Set risk" on the selected addresses and nothing else. A
 * selection with no address (transactions only) has nothing to offer here.
 */
function BulkRiskBar({ addresses, onChanged }: BulkAttributionBarProps): React.ReactElement | null {
  const [running, setRunning] = React.useState(false);
  const { canSetRisk, applyRisk } = useBulkRisk(addresses, onChanged, setRunning);
  if (!canSetRisk || addresses.length === 0) return null;
  if (addresses.length > MAX_BULK_SUBJECTS) {
    return (
      <span className="text-xs text-muted-foreground">
        Select {MAX_BULK_SUBJECTS} or fewer addresses to set their risk together.
      </span>
    );
  }
  return <RiskSelect count={addresses.length} running={running} onPick={applyRisk} />;
}

/**
 * Set one risk band on every selected address.
 *
 * The workflow gap this closes: the only two writers of `risk_level` were the
 * address edit dialog and the case inspector's control, both single-address,
 * so triaging fifty flagged counterparties meant fifty dialogs — and the
 * screen where a reader has just finished deciding they are all bad is this
 * one.
 *
 * Deliberately NOT an {@link AttributionTarget}. The three stores are catalog
 * claims resolved through the tenancy ladder; a risk band is a field on the
 * address row, written by `PATCH /addresses/{chain}/{address}`. That is also why
 * it is BASIC on every plan: packaging follows the capability (editing an
 * address's risk), not the surface it is offered from.
 *
 * Everything else follows the bar's conventions, because the failure modes are
 * identical: `applyToMany` for the cap and the concurrency limit, the subjects
 * already deduplicated by `(chain, address)` upstream, and a partial write
 * raised as an ERROR with counts rather than a cheerful toast — half a triage
 * is the case that needs somebody to go and look.
 *
 * The risk band is a write on the ADDRESS ROW, not a catalog attachment, so it
 * is gated on the address permission rather than on the host's `canEdit` —
 * which on a transaction feed is about attributing transactions.
 */
function useBulkRisk(
  subjects: readonly AttributionSubject[],
  onChanged: () => void,
  setRunning: (running: boolean) => void,
): { canSetRisk: boolean; applyRisk: (level: string) => Promise<void> } {
  const addressesApi = useAddressesApi();
  const invalidateAddresses = useInvalidateAddressOverlays();
  const { can } = usePermissions();
  const canSetRisk = can(RESOURCES.addresses, "edit");
  const applyRisk = React.useCallback(
    async (level: string): Promise<void> => {
      if (!level) return;
      setRunning(true);
      try {
        const outcome = await applyToMany([...subjects], async (subject) => {
          if (subject.kind !== "address") return;
          await addressesApi.update(subject.chainId, subject.address, { risk_level: level });
        });
        const message = outcomeMessage(
          outcome,
          `${targetNoun("address", subjects.length)} set to ${level}`,
        );
        if (outcome.failed.length === 0) {
          notifySuccess(message, { title: "Risk level" });
        } else {
          notifyError(new Error(message), { title: "Couldn't finish setting the risk level" });
        }
        // Every table, graph node and counterparty column drawing these
        // addresses, not just the list that was selected from — the same shared
        // invalidation each single-address writer uses.
        invalidateAddresses();
        onChanged();
      } finally {
        setRunning(false);
      }
    },
    [addressesApi, invalidateAddresses, onChanged, setRunning, subjects],
  );
  return { canSetRisk, applyRisk };
}

/** The "Set risk" control, shared by both bars. */
function RiskSelect({
  count,
  running,
  onPick,
}: {
  count: number;
  running: boolean;
  onPick: (level: string) => Promise<void>;
}): React.ReactElement {
  return (
    <span className="flex items-center">
      {/* Named through `htmlFor`, not `aria-label`: `EnumSelect` does not
          forward arbitrary attributes to its trigger, and TypeScript waves
          hyphenated JSX attributes through — so an `aria-label` here would
          typecheck, render nothing, and leave the control unnamed. */}
      <Label htmlFor={RISK_SELECT_ID} className="sr-only">
        Set risk on {targetLabel("address", count)}
      </Label>
      <EnumSelect
        id={RISK_SELECT_ID}
        value=""
        onChange={(next) => void onPick(next)}
        options={RISK_OPTIONS}
        placeholder="Set risk"
        disabled={running}
        className="h-7 w-28 text-xs"
      />
    </span>
  );
}

/** With `catalog_advanced`: every bulk catalog action, plus "Set risk". */
function BulkCatalogAttributionBar({
  transactions,
  addresses,
  shared,
  canEdit,
  onChanged,
  preferredEntity,
  stores = ["overlay"],
}: BulkAttributionBarProps): React.ReactElement | null {
  const catalog = useCatalogApi();
  const attribution = useAttributionController({ shared });
  const [running, setRunning] = React.useState(false);
  const [store, setStore] = React.useState<AttributionTarget>(stores[0] ?? "overlay");
  // The role a membership write carries. Kept beside the store rather than in the
  // attributes panel: it is a membership-only field, and the panel is shared with
  // the overlay routes that would discard it.
  const [role, setRole] = React.useState("");
  // Defaults to the literal reading of the checkboxes. Attributing sixty-three
  // counterparties off a forty-row selection is a bigger claim than the user
  // made with the gesture, so it stays one deliberate click away.
  const [preferred, setPreferred] = React.useState<BulkTarget>("transaction");

  const bySubject: Record<BulkTarget, AttributionSubject[]> = React.useMemo(
    () => ({ transaction: transactions, address: addresses }),
    [transactions, addresses],
  );
  // Membership and the headline pointer are keyed by `(chain, address)`, so with
  // one of those chosen the transaction reading is not a choice — `planAttribution`
  // would throw on it. Forced rather than offered-and-refused.
  const addressOnly = store !== "overlay";
  // Falls back rather than sticking: a selection can stop having addresses (rows
  // with no chain have nowhere to write to), and an empty target would leave the
  // bar offering pickers that write to nothing.
  const target = addressOnly
    ? "address"
    : bySubject[preferred].length > 0
      ? preferred
      : OTHER[preferred];
  const subjects = bySubject[target];

  // Greys the candidate rows against ONE member of the batch, which is sound for
  // the rules that turn on the subject's kind — every member of a batch shares
  // one. The per-subject judgement happens in the controller, which re-checks
  // each write against its own subject, so a future rule keying on a subject's
  // chain refuses the members it should rather than being decided by whichever
  // row happened to sort first.
  const loaders = useCatalogLoaders(subjects[0]);

  const overCap = subjects.length > MAX_BULK_SUBJECTS;
  const teams = useMyTeams();

  const applyKind = React.useCallback(
    async (
      kind: AttributionKind,
      option: AttributionEditorCandidate,
      attributes: AssignmentAttributes = {},
    ): Promise<void> => {
      setRunning(true);
      try {
        const outcome = await applyToMany(subjects, (subject) =>
          attribution.assign({
            subject,
            kind,
            candidate: option,
            attributes,
            target: store,
            role: store === "membership" ? role : undefined,
          }),
        );
        // Names the reading as well as the count: "12 addresses attributed" and
        // "40 transactions attributed" come from the same click on the same
        // selection, and a bare "12 attributed" would not say which happened.
        const message = outcomeMessage(
          outcome,
          `${targetNoun(target, subjects.length)} ${STORE_PAST_TENSE[store] ?? PAST_TENSE[kind]}`,
        );
        if (outcome.failed.length === 0) {
          notifySuccess(message, { title: option.name });
        } else {
          // Reported as an error rather than a warning toast: a half-applied
          // attribution needs someone to go and look, and the ones that failed
          // are still sitting unselected in the table.
          notifyError(new Error(message), { title: `Couldn't finish ${option.name}` });
        }
        onChanged();
      } finally {
        setRunning(false);
      }
    },
    [attribution, onChanged, role, store, subjects, target],
  );

  const { canSetRisk, applyRisk } = useBulkRisk(subjects, onChanged, setRunning);

  // `form` mode: the pick is held and the panel asks before anything is written.
  // Judged against one member of the batch, like the candidate loaders above —
  // every member shares the subject kind the capability turns on, and the
  // controller re-prunes each write against its own subject.
  //
  // The headline pointer is the exception: it carries no provenance, so there is
  // nothing to ask and a panel offering confidence would collect fields the route
  // discards. It writes on the pick.
  const flow = usePendingAttribution({
    subject: subjects[0]!,
    commit: store === "primary" ? "on-pick" : "form",
    defaults: (kind) => ({ visibility: attribution.defaultVisibility(kind) }),
    onCommit: applyKind,
  });

  if (!canEdit || subjects.length === 0) return null;

  // Only worth asking when both readings exist. One of them empty means there is
  // no choice to make, and a switch with a dead option is just noise. Suppressed
  // entirely under an authoritative store, where the reading is forced.
  const choice =
    !addressOnly && transactions.length > 0 && addresses.length > 0 ? (
      <span className="flex items-center gap-1 rounded-md border p-0.5">
        {(["transaction", "address"] as const).map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={target === option ? "secondary" : "ghost"}
            className="h-6 px-2 text-xs"
            aria-pressed={target === option}
            disabled={running}
            onClick={() => setPreferred(option)}
          >
            {targetLabel(option, bySubject[option].length)}
          </Button>
        ))}
      </span>
    ) : null;

  const storeChoice =
    stores.length > 1 ? (
      <span className="flex items-center gap-1 rounded-md border p-0.5">
        {stores.map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={store === option ? "secondary" : "ghost"}
            className="h-6 px-2 text-xs"
            aria-pressed={store === option}
            title={STORE_HINT[option]}
            disabled={running}
            onClick={() => setStore(option)}
          >
            {STORE_LABEL[option]}
          </Button>
        ))}
      </span>
    ) : null;

  return (
    <span className="flex items-center gap-1.5">
      {storeChoice}
      {choice}
      {store === "membership" && !overCap ? (
        <RoleCombobox
          value={role}
          onChange={setRole}
          disabled={running}
          className="w-40"
          placeholder="Role (optional)"
        />
      ) : null}
      {overCap ? (
        // The switch stays reachable: the other reading is usually far smaller
        // (forty rows are often a handful of counterparties), so switching is
        // frequently the answer to being over the cap.
        <span className="text-xs text-muted-foreground">
          Select {MAX_BULK_SUBJECTS} or fewer {target === "address" ? "addresses" : "transactions"}{" "}
          to attribute them together.
        </span>
      ) : null}
      {/* Addresses only, and only under the address permission: there is no risk
          field on a transaction — `PATCH /transactions/{id}` forbids one — so on
          the transaction reading this would be a control that cannot write. */}
      {!overCap && target === "address" && canSetRisk ? (
        <RiskSelect count={subjects.length} running={running} onPick={applyRisk} />
      ) : null}
      {!overCap && preferredEntity ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={running}
          onClick={() =>
            void applyKind("entity", { id: preferredEntity.id, name: preferredEntity.name })
          }
        >
          <UserCheck className="mr-1.5 h-3.5 w-3.5" />
          {preferredEntity.name}
        </Button>
      ) : null}
      {!overCap &&
        STORE_KINDS[store].map((kind) => (
          <CatalogItemPicker
            key={kind}
            label={KIND_LABEL[kind]}
            loadCandidates={loaders[kind]}
            busy={running}
            disabled={running}
            resolveLogoUrl={catalog.resolveLogoUrl}
            onPick={(option) => flow.pick(kind, option)}
          />
        ))}

      {/* Modal rather than a popover: the bar lives in a table toolbar with no
          room to expand, and a claim over this many rows is worth the
          interruption. */}
      <Dialog open={Boolean(flow.held)} onOpenChange={(open) => !open && flow.cancel()}>
        <DialogContent className="sm:max-w-md">
          {flow.held ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {store === "membership" ? "Map" : IMPERATIVE[flow.held.kind]}{" "}
                  {targetLabel(target, subjects.length)}
                </DialogTitle>
                <DialogDescription>
                  {store === "membership"
                    ? `Written to the tenant-wide company map, once per ${targetNoun(target, 1)}.`
                    : `Recorded once per ${targetNoun(target, 1)}, each carrying what you enter here.`}
                </DialogDescription>
              </DialogHeader>
              <AttributionAttributesPanel
                subject={subjects[0]!}
                active={flow.held}
                teamOptions={teams}
                busy={running}
                confirmLabel="Attach"
                onChange={flow.setAttributes}
                onCancel={flow.cancel}
                onSave={flow.save}
              />
              {flow.error ? (
                <p role="alert" className="text-sm text-destructive">
                  {flow.error}
                </p>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </span>
  );
}
